import { CalendarDate } from '@internationalized/date';
import dayjs from 'dayjs';
import { computed, shallowRef, toRef } from 'vue';
import type { MaybeRefOrGetter } from 'vue';
import * as zod from 'zod';

import type { UserOOOPreferences } from '@stuut/api-sdk/models';
import { toast } from '@ui/components/sonner';

import {
  useCreateOutOfOffice,
  useOutOfOfficePreferencesQuery,
  useUpdateOutOfOffice,
} from '@/composables/useUserOutOfOffice';
import { useUsersList } from '@/composables/useUsers';
import { getApiErrorMessage } from '@/utils/apiErrors';

import { getOutOfOfficeSaveErrorMessage } from './outOfOfficeErrors';

interface UseOutOfOfficeResponderFormOptions {
  currentUserId?: MaybeRefOrGetter<string | undefined>;
  hasExistingOutOfOffice?: MaybeRefOrGetter<boolean | undefined>;
  targetUserId: MaybeRefOrGetter<string>;
}

function getTodayCalendarDate(): CalendarDate {
  const today = dayjs();
  return new CalendarDate(today.year(), today.month() + 1, today.date());
}

// CalendarDate.toString() returns 'YYYY-MM-DD'. Native Date / dayjs both parse
// that as UTC midnight, which renders as the previous day for negative-offset
// timezones — so we read the calendar fields directly when available and only
// fall back to string parsing for unrecognized inputs.
const readCalendarFields = (
  value: unknown,
): { day: number; month: number; year: number } | null => {
  if (value instanceof CalendarDate) {
    return { day: value.day, month: value.month, year: value.year };
  }
  if (value && typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      return { day: Number(match[3]), month: Number(match[2]), year: Number(match[1]) };
    }
  }
  return null;
};

/** Accepts DateValue / CalendarDate from Calendar without fighting vue-tsc private-field unions. */
export const formatDateLabel = (value: unknown): string | null => {
  const fields = readCalendarFields(value);
  if (fields) {
    return dayjs(new Date(fields.year, fields.month - 1, fields.day)).format('MMMM D, YYYY');
  }
  if (value == null || typeof value !== 'object' || !('toString' in value)) return null;
  return dayjs(String(value)).format('MMMM D, YYYY');
};

const toRequestDate = (value: unknown): Date | undefined => {
  const fields = readCalendarFields(value);
  if (fields) return new Date(Date.UTC(fields.year, fields.month - 1, fields.day));
  if (!value) return undefined;
  return dayjs(String(value)).toDate();
};

const toCalendarDate = (value: unknown): CalendarDate | null => {
  if (!value) return null;
  if (value instanceof Date) {
    return new CalendarDate(value.getUTCFullYear(), value.getUTCMonth() + 1, value.getUTCDate());
  }
  if (typeof value === 'string') {
    const dateParts = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateParts) {
      return new CalendarDate(Number(dateParts[1]), Number(dateParts[2]), Number(dateParts[3]));
    }
  }
  const date = dayjs(String(value));
  if (!date.isValid()) return null;
  return new CalendarDate(date.year(), date.month() + 1, date.date());
};

const getStringProperty = (value: unknown, property: string): string | null => {
  if (value == null || typeof value !== 'object') return null;

  const propertyValue = (value as Record<string, unknown>)[property];
  return typeof propertyValue === 'string' ? propertyValue : null;
};

export interface OutOfOfficeFormValues {
  backupUserId: string | null;
  firstDay: CalendarDate | null | undefined;
  lastDay: CalendarDate | null | undefined;
}

const calendarDateSchema = zod.custom<CalendarDate>(
  (value) => value instanceof CalendarDate,
  'Select a valid date',
);

export const outOfOfficeFormSchema = zod
  .object({
    backupUserId: zod
      .string()
      .nullish()
      .refine((v): v is string => !!v && v.length > 0, 'Select a backup user'),
    // Calendar emits `undefined` when a selected date is clicked again, so accept
    // both null and undefined and rely on `.refine` for required-day messaging.
    firstDay: calendarDateSchema
      .nullish()
      .refine((v): v is CalendarDate => v != null, 'Select a first day'),
    lastDay: calendarDateSchema.nullish(),
  })
  .refine(
    (data) => {
      if (!data.firstDay || !data.lastDay) return true;
      return !dayjs(data.lastDay.toString()).isBefore(dayjs(data.firstDay.toString()), 'day');
    },
    { message: 'Last day must be on or after first day', path: ['lastDay'] },
  );

export interface OutOfOfficeFormDefaults {
  backupUserId?: string | null;
  firstDay?: unknown;
  lastDay?: unknown;
}

export interface SharedInboxEmailSender {
  email: string;
  id: string;
  is_shared?: boolean;
  user_id?: string | null;
}

export interface SharedInboxCandidateOptions {
  currentUserId?: string;
  email?: string;
  emailSenders: SharedInboxEmailSender[];
}

export interface UserOutOfOfficeState {
  backupUserId: string | null;
  isOutOfOffice: boolean;
}

const getBooleanProperty = (value: unknown, property: string): boolean | null => {
  if (value == null || typeof value !== 'object') return null;

  const propertyValue = (value as Record<string, unknown>)[property];
  return typeof propertyValue === 'boolean' ? propertyValue : null;
};

export const getSharedInboxCandidate = ({
  currentUserId,
  email,
  emailSenders,
}: SharedInboxCandidateOptions): SharedInboxEmailSender | null => {
  if (!currentUserId || !email) return null;

  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail) return null;

  return (
    emailSenders.find(
      (sender) =>
        sender.email.trim().toLowerCase() === normalizedEmail &&
        sender.user_id === currentUserId &&
        !sender.is_shared,
    ) ?? null
  );
};

export const getOutOfOfficeSubmitLabel = (hasExistingOutOfOffice: boolean) =>
  hasExistingOutOfOffice ? 'Update' : 'Save';

export const getUserOutOfOfficeState = (user: unknown): UserOutOfOfficeState => ({
  backupUserId:
    getStringProperty(user, 'backupUserId') ?? getStringProperty(user, 'backup_user_id'),
  isOutOfOffice: getBooleanProperty(user, 'isOoo') ?? getBooleanProperty(user, 'is_ooo') ?? false,
});

export const isUserOutOfOffice = (user: unknown): boolean =>
  getUserOutOfOfficeState(user).isOutOfOffice;

export const getInitialOutOfOfficeFormValues = (
  preferences: UserOOOPreferences | null,
  defaults?: string | OutOfOfficeFormDefaults,
): OutOfOfficeFormValues => {
  const defaultValues = typeof defaults === 'string' ? { backupUserId: defaults } : defaults;

  if (!preferences) {
    return {
      backupUserId: defaultValues?.backupUserId ?? null,
      firstDay: toCalendarDate(defaultValues?.firstDay) ?? getTodayCalendarDate(),
      lastDay: toCalendarDate(defaultValues?.lastDay),
    };
  }

  if (typeof defaults === 'object') {
    return {
      backupUserId:
        defaultValues?.backupUserId ??
        getStringProperty(preferences, 'defaultBackupUserId') ??
        null,
      firstDay:
        toCalendarDate(defaultValues?.firstDay) ??
        toCalendarDate(getStringProperty(preferences, 'startDate')) ??
        getTodayCalendarDate(),
      lastDay:
        toCalendarDate(defaultValues?.lastDay) ??
        toCalendarDate(getStringProperty(preferences, 'endDate')),
    };
  }

  return {
    backupUserId:
      getStringProperty(preferences, 'defaultBackupUserId') ?? defaultValues?.backupUserId ?? null,
    firstDay: toCalendarDate(getStringProperty(preferences, 'startDate')) ?? getTodayCalendarDate(),
    lastDay: toCalendarDate(getStringProperty(preferences, 'endDate')),
  };
};

export function useOutOfOfficeResponderForm(options: UseOutOfOfficeResponderFormOptions) {
  const targetUserId = toRef(options.targetUserId);
  const currentUserId = options.currentUserId
    ? toRef(options.currentUserId)
    : shallowRef(undefined);
  const hasExistingOutOfOffice = options.hasExistingOutOfOffice
    ? toRef(options.hasExistingOutOfOffice)
    : shallowRef(false);

  const usersQuery = useUsersList({ includeDeactivated: true });
  const preferencesQuery = useOutOfOfficePreferencesQuery(targetUserId);
  const createMutation = useCreateOutOfOffice();
  const updateMutation = useUpdateOutOfOffice();

  const users = computed(() => usersQuery.data.value?.data ?? []);
  const loadedPreferences = computed(() => preferencesQuery.data.value ?? null);
  const isLoading = computed(() => usersQuery.isLoading.value || preferencesQuery.isLoading.value);
  const isSaving = computed(() => createMutation.isPending.value || updateMutation.isPending.value);
  const loadError = computed(() =>
    preferencesQuery.error.value
      ? getApiErrorMessage(preferencesQuery.error.value, 'Could not load out-of-office settings')
      : null,
  );

  const activeBackupUsers = computed(() =>
    users.value.filter(
      (user) =>
        !user.isAccDisabled && user.id !== targetUserId.value && user.id !== currentUserId.value,
    ),
  );

  const save = async (values: OutOfOfficeFormValues) => {
    if (isSaving.value) return false;
    if (loadError.value) {
      toast.error('Resolve the load error before saving');
      return false;
    }
    if (!values.backupUserId || !values.firstDay) return false;

    const payload = {
      body: {
        backupUserId: values.backupUserId,
        endDate: values.lastDay ? toRequestDate(values.lastDay) : null,
        startDate: toRequestDate(values.firstDay),
      },
      userId: targetUserId.value,
    };

    try {
      if (hasExistingOutOfOffice.value) {
        await updateMutation.mutateAsync(payload);
      } else {
        await createMutation.mutateAsync(payload);
      }
      toast.success('Out of office settings saved.');
      return true;
    } catch (err: unknown) {
      toast.error(getOutOfOfficeSaveErrorMessage(err));
      return false;
    }
  };

  return {
    activeBackupUsers,
    isLoading,
    isSaving,
    loadError,
    loadedPreferences,
    save,
    users,
  };
}
